import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from '../Avatar/Avatar';
import { useAppStore } from '../../store/appStore';
import { apiClient } from '../../services/api';
import './languageSelector.css';

interface LanguageSelectorProps {
  onLanguageSelect: (lang: string) => void;
}

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onLanguageSelect }) => {
  const { t, i18n } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const setAvatarSpeaking = useAppStore((state) => state.setAvatarSpeaking);
  const setAvatarLoading = useAppStore((state) => state.setAvatarLoading);
  const setCurrentVisemes = useAppStore((state) => state.setCurrentVisemes);
  const setAudioElement = useAppStore((state) => state.setAudioElement);
  const addMessage = useAppStore((state) => state.addMessage);

  const languages = [
    { code: 'it', name: 'Italiano', flag: 'https://flagcdn.com/w320/it.png' },
    { code: 'en', name: 'English', flag: 'https://flagcdn.com/w320/gb.png' },
    { code: 'fr', name: 'Français', flag: 'https://flagcdn.com/w320/fr.png' },
    { code: 'de', name: 'Deutsch', flag: 'https://flagcdn.com/w320/de.png' },
    { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w320/es.png' },
    { code: 'pt', name: 'Português', flag: 'https://flagcdn.com/w320/pt.png' },
    { code: 'nl', name: 'Nederlands', flag: 'https://flagcdn.com/w320/nl.png' },
    { code: 'pl', name: 'Polski', flag: 'https://flagcdn.com/w320/pl.png' },
    { code: 'ru', name: 'Русский', flag: 'https://flagcdn.com/w320/ru.png' },
    { code: 'zh', name: '中文', flag: 'https://flagcdn.com/w320/cn.png' },
    { code: 'ja', name: '日本語', flag: 'https://flagcdn.com/w320/jp.png' },
    { code: 'ar', name: 'العربية', flag: 'https://flagcdn.com/w320/sa.png' },
  ];

  const handleLanguageClick = async (langCode: string) => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      console.log('[LanguageSelector] Changing language to:', langCode);

      // Change language and wait for it to complete
      await i18n.changeLanguage(langCode);
      localStorage.setItem('selectedLanguage', langCode);

      console.log('[LanguageSelector] Language changed to:', i18n.language);
      console.log('[LanguageSelector] Available languages:', Object.keys(i18n.options.resources || {}));

      // Get greeting message directly with language specification
      const greeting = i18n.t('ai.greeting', { lng: langCode });

      // Get instructions text
      const instructionsMode1 = i18n.t('booking.instructions.mode1', { lng: langCode });
      const instructionsMode2 = i18n.t('booking.instructions.mode2', { lng: langCode });
      const instructionsText = `${instructionsMode1}. ${instructionsMode2}`;

      // Combine greeting and instructions for a single TTS call (better for mobile autoplay)
      const fullText = `${greeting} ${instructionsText}`;

      console.log('[LanguageSelector] Greeting text:', greeting);
      console.log('[LanguageSelector] Instructions text:', instructionsText);
      console.log('[LanguageSelector] Full text length:', fullText.length);

      // Add greeting message to store
      addMessage({
        id: `msg-${Date.now()}`,
        role: 'assistant',
        content: greeting,
        timestamp: Date.now(),
      });

      // Set loading state
      setAvatarLoading(true);

      // Synthesize greeting + instructions as a single audio (fixes mobile autoplay)
      console.log('[LanguageSelector] Synthesizing greeting + instructions in', langCode);
      const ttsResponse = await apiClient.synthesize({
        text: fullText,
        language: langCode,
      });

      // Remove loading state
      setAvatarLoading(false);

      // Play audio (now contains greeting + instructions in a single audio)
      if (ttsResponse.audio_base64) {
        const audioDataUrl = `data:audio/mpeg;base64,${ttsResponse.audio_base64}`;
        const audio = new Audio(audioDataUrl);

        audio.onended = () => {
          console.log('[LanguageSelector] Audio (greeting + instructions) ended, proceeding to main app');
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
          // Mark instructions as already spoken
          useAppStore.getState().setInstructionsSpoken(true);
          // Proceed to main app
          onLanguageSelect(langCode);
        };

        audio.onerror = (event) => {
          console.error('[LanguageSelector] Audio error:', event);
          setAvatarSpeaking(false);
          setCurrentVisemes([]);
          setAudioElement(null);
          // Proceed anyway
          onLanguageSelect(langCode);
        };

        // Set state before playing
        setAudioElement(audio);
        setAvatarSpeaking(true);
        if (ttsResponse.visemes && ttsResponse.visemes.length > 0) {
          console.log('[LanguageSelector] Setting visemes:', ttsResponse.visemes.length);
          setCurrentVisemes(ttsResponse.visemes);
        }

        // Play audio
        const playPromise = audio.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('[LanguageSelector] Audio playback started (greeting + instructions)');
            })
            .catch((error) => {
              console.error('[LanguageSelector] Playback failed:', error);
              setAvatarSpeaking(false);
              setCurrentVisemes([]);
              setAudioElement(null);
              // Proceed anyway
              onLanguageSelect(langCode);
            });
        }
      } else {
        // No audio, proceed immediately
        console.warn('[LanguageSelector] No audio received from TTS');
        onLanguageSelect(langCode);
      }
    } catch (error) {
      console.error('[LanguageSelector] Error:', error);
      setIsProcessing(false);
      setAvatarLoading(false);
      // Proceed anyway
      onLanguageSelect(langCode);
    }
  };

  return (
    <div className="language-selector-wrapper">
      <header className="language-selector-header">
        <div className="language-header-content">
          <div className="language-logo-section">
            <img
              src="https://www.caladeisardi.it/wp-content/uploads/2022/10/logo_CDS-1.png"
              alt="Cala dei Sardi Logo"
              className="language-logo"
            />
          </div>
          <h1>{t('languageSelector.title')}</h1>
        </div>
      </header>

      <div className="language-selector-container">
        <div className="language-selector-content">
          <div className="language-avatar-section">
            <Avatar />
          </div>

          <div className="language-flags-grid">
            {languages.map((lang) => (
              <button
                key={lang.code}
                className="language-flag-button"
                onClick={() => handleLanguageClick(lang.code)}
                aria-label={`Select ${lang.name}`}
                disabled={isProcessing}
              >
                <img src={lang.flag} alt={lang.name} className="flag-icon" />
                <span className="language-name">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
